import { useCallback } from "react";

import { FeedbackMode, FeedbackSeverity, useFeedbackStore } from "store/useFeedbackStore";

type ShowFeedbackParams = {
  severity: FeedbackSeverity;
  message: string;
  title?: string;
  details?: string;
  mode?: FeedbackMode;
};

const formatJsonBlock = (value: unknown): string | undefined => {
  if (!value) return undefined;

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return undefined;
  }
};

const extractErrorDetails = (error: unknown): string | undefined => {
  if (!error) return undefined;
  if (typeof error === "string") return error;

  const backendError = error as {
    message?: string;
    response?: {
      data?: {
        success?: boolean;
        message?: string;
        error?: string;
        errors?: Record<string, string[]>;
      };
    };
  };

  const responseData = backendError.response?.data;
  const technicalMessage =
    responseData?.message ||
    responseData?.error ||
    backendError.message;

  const errorsPayload = responseData?.errors ? formatJsonBlock(responseData) : undefined;

  if (technicalMessage && errorsPayload) {
    return `Mensagem técnica:\n${technicalMessage}\n\nJSON de retorno:\n${errorsPayload}`;
  }

  if (technicalMessage || errorsPayload) {
    return technicalMessage || errorsPayload;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return undefined;
};

export const useAppFeedback = () => {
  const showFeedback = useFeedbackStore((state) => state.showFeedback);
  const closeFeedback = useFeedbackStore((state) => state.closeFeedback);

  const show = useCallback(
    ({ severity, message, title, details, mode }: ShowFeedbackParams) => {
      showFeedback({
        severity,
        message,
        title,
        details,
        mode,
      });
    },
    [showFeedback],
  );

  const showSuccess = useCallback(
    (message: string, mode: FeedbackMode = "snackbar", title?: string) => {
      show({
        severity: "success",
        message,
        mode,
        title,
      });
    },
    [show],
  );

  const showError = useCallback(
    (message: string, error?: unknown, mode: FeedbackMode = "dialog", title?: string) => {
      show({
        severity: "error",
        message,
        mode,
        title,
        details: extractErrorDetails(error),
      });
    },
    [show],
  );

  return {
    show,
    showSuccess,
    showError,
    closeFeedback,
  };
};
